import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {NucleotideType} from 'biojs/BioConstants';
import {sanitize, getSanitizedBasesAndSanitizationErrors} from 'biojs/Util';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import {getBaseCoercionWarningText} from 'molbio/MolBioUtil';
import {trackEnterValueInNucleotideCreateModal} from 'molbio/SequenceAnalyticsHelper';
import FormGroup from 'ui-library/bs/FormGroup';
import {InputErrorMessageStyle, LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';

import {SequenceImportOptions, RawImportData} from './ImportSequenceConstants';
import SequenceImportOptionsForm from './SequenceImportOptionsForm';

export interface CreateSequenceFormProps {
  sequenceImportOptions: SequenceImportOptions;
  setSequenceImportOptions: (options: SequenceImportOptions) => void;
  rawImportData: RawImportData;
  setRawImportData: (data: RawImportData) => void;
}
function CreateSequenceForm({
  sequenceImportOptions,
  setSequenceImportOptions,
  rawImportData,
  setRawImportData,
}: CreateSequenceFormProps): JSX.Element {
  // track previous values to only log analytic event if input changes
  const [prevName, setPrevName] = useState(rawImportData.name);
  const [prevBases, setPrevBases] = useState(rawImportData.bases);
  const [basesCoerced, setBasesCoerced] = useState(false);

  function logIfFieldValueChange(
    newVal: string,
    curVal: string,
    curValSetter: React.Dispatch<React.SetStateAction<string>>,
    fieldName: string
  ): void {
    if (curVal !== newVal) {
      trackEnterValueInNucleotideCreateModal(`${sequenceImportOptions.nucleotideType} sequence`, fieldName);
      curValSetter(newVal);
    }
  }
  return (
    <div>
      <FormGroup className={css(styles.nameSection)}>
        <TextField
          label="Name"
          labelStyle={LabelStyle.LABEL}
          isRequired={true}
          value={rawImportData.name}
          onChange={(e) => {
            setRawImportData({...rawImportData, name: e.target.value});
          }}
          onBlur={() => {
            logIfFieldValueChange(rawImportData.name, prevName, setPrevName, 'name');
          }}
          dataTestAttrs={{component: 'CreateSequenceForm', element: 'nameFieldInput'}}
        />
      </FormGroup>
      <SequenceImportOptionsForm
        multipleSequences={false}
        sequenceImportOptions={sequenceImportOptions}
        folderIdOptions={{
          onChange: (folderId) => {
            setSequenceImportOptions({...sequenceImportOptions, folderId});
          },
        }}
        nucleotideTypeOptions={{
          onChange: (nucleotideType) => {
            analytics.track('sequence', 'toggle_nucleotide_type', {
              product: ANALYTICS_MODULE.MOLBIO,
              importFlow: DNA_SEQUENCE_IMPORT_FLOW.RAW,
              nucleotideType,
              liveEnsemblUI: true,
            });
            setSequenceImportOptions({
              ...sequenceImportOptions,
              nucleotideType,
              topology:
                nucleotideType === NucleotideType.RNA
                  ? SequenceTopology.LINEAR
                  : sequenceImportOptions.topology,
            });
            setBasesCoerced(false);
            const newBases = sanitize(rawImportData.bases, nucleotideType);
            setRawImportData({
              ...rawImportData,
              bases: newBases,
            });
            setPrevBases(newBases);
          },
        }}
        topologyOptions={{
          isDisabled: sequenceImportOptions.nucleotideType === NucleotideType.RNA,
          disabledHelpText: 'We do not currently support circular RNA sequences.',
          onChange: (topology) => {
            setSequenceImportOptions({...sequenceImportOptions, topology});
          },
        }}
        tagSchemaIdOptions={{
          onChange: (tagSchemaId) => {
            setSequenceImportOptions({...sequenceImportOptions, tagSchemaId});
          },
        }}
        studySearchItemOptions={{
          onChange: (studySearchItems) => {
            setSequenceImportOptions({...sequenceImportOptions, studySearchItems});
          },
        }}
      />
      <FormGroup className={css(styles.basesSection)}>
        <TextField
          label="Bases"
          labelStyle={LabelStyle.LABEL}
          error={
            basesCoerced
              ? {
                  message: getBaseCoercionWarningText(sequenceImportOptions.nucleotideType),
                  style: InputErrorMessageStyle.INFO,
                }
              : null
          }
          value={rawImportData.bases}
          dataTestAttrs={{component: 'CreateSequenceForm', element: 'baseFieldInput'}}
          onChange={(e) => {
            const {
              sanitizedBases,
              errors: {convertedUToT, convertedTToU},
            } = getSanitizedBasesAndSanitizationErrors(e.target.value, sequenceImportOptions.nucleotideType);
            if (convertedUToT || convertedTToU) {
              setBasesCoerced(true);
            }
            setRawImportData({
              ...rawImportData,
              bases: sanitizedBases,
            });
          }}
          onBlur={() => {
            logIfFieldValueChange(rawImportData.bases, prevBases, setPrevBases, 'bases');
          }}
        />
      </FormGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  nameSection: {
    width: '50%',
  },
  basesSection: {
    marginTop: '4px',
  },
});

export default CreateSequenceForm;
